import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';

const catalogCardVariants = cva(
	'relative h-full overflow-hidden rounded-xl border p-2 transition-all duration-300 hover:shadow-md md:p-8',
	{
		variants: {
			variant: {
				default: 'border-gray-200 bg-white text-black shadow-sm',
				emerald:
					'border-green-300 bg-gradient-to-br from-green-900 via-green-600 to-green-300 text-white',
				aurora:
					'border-green-700 bg-[radial-gradient(circle_at_85%_15%,rgba(128,227,176,0.5),transparent_34%),radial-gradient(circle_at_18%_90%,rgba(25,201,116,0.28),transparent_40%),linear-gradient(145deg,#11151A_10%,#064E3B_58%,#086040_100%)] text-white ',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
);

export const catalogCardDescriptionVariants = cva('mb-2 text-sm md:text-base', {
	variants: {
		variant: {
			default: 'line-clamp-2 text-gray-3',
			emerald: 'text-green-50',
			aurora: 'text-green-100',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

const CatalogCardDecoration = ({
	decoration,
}: {
	decoration: 'offset-square' | 'ambient-glow';
}) => {
	if (decoration === 'ambient-glow') {
		return (
			<div aria-hidden="true" className="pointer-events-none absolute inset-0">
				<div className="absolute -right-8 -top-12 h-40 w-40 rounded-full bg-green-200/35 blur-3xl" />
				<div className="absolute -bottom-14 -left-8 h-40 w-40 rounded-full bg-green-400/25 blur-3xl" />
				<div className="absolute -right-16 top-8 h-16 w-72 rotate-[-28deg] bg-gradient-to-r from-transparent via-white/15 to-transparent blur-sm" />
			</div>
		);
	}

	return (
		<div aria-hidden="true" className="pointer-events-none absolute inset-0">
			<div className="absolute -right-12 -top-20 h-52 w-52 rounded-full bg-white/25 blur-3xl" />
			<div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.32)_1px,transparent_1px)] opacity-30 [background-size:18px_18px]" />
			<div className="absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/70 to-transparent" />

			<div className="absolute bottom-4 right-4 h-[72px] w-[72px] translate-x-1 translate-y-1 rotate-[9deg] rounded-sm border border-white/45" />
			<div className="absolute bottom-6 right-6 h-[72px] w-[72px] rotate-[16deg] rounded-sm border border-white/25 bg-gradient-to-br from-white/25 to-green-100/10 shadow-[0_12px_30px_-14px_rgba(6,78,59,0.75)]" />
		</div>
	);
};

export const catalogCardTitleClass = 'mb-2 text-base font-semibold md:text-lg';

export function CatalogCard({
	variant = 'default',
	className = '',
	children,
}: {
	variant?: 'default' | 'emerald' | 'aurora';
	className?: string;
	children: ReactNode;
}) {
	return (
		<div className={`${catalogCardVariants({ variant })} ${className}`}>
			{variant !== 'default' && (
				<CatalogCardDecoration
					decoration={variant === 'emerald' ? 'offset-square' : 'ambient-glow'}
				/>
			)}
			{children}
		</div>
	);
}
